import { ApolloClient } from '@apollo/client';

import { type GhostContentType } from '@pflegia/types/common/magazin/ghost-content-type';
import { isDefined } from '@pflegia/util/functions/type-guards';

import { AuthorType, CategoryType, PostsResponse } from '../components/magazin/types';
import { GET_GHOST_AUTHOR_BY_SLUG } from '../graphql/operations/ghost-author-by-slug';
import { GET_GHOST_AUTHORS } from '../graphql/operations/ghost-authors';
import { GET_GHOST_CATEGORIES } from '../graphql/operations/ghost-categories';
import { GET_GHOST_POSTS } from '../graphql/operations/ghost-posts';

export const MAGAZIN_ALL_CATEGORIES_SLUG = 'all';

export const MAGAZIN_UTM_CONTENT = {
  currentJobsJobTitle: 'current-jobs-job-title',
  currentJobsMoreInfo: 'current-jobs-more-info',
  currentJobsInterested: 'current-jobs-interested',
  currentJobsAllJobs: 'current-jobs-all-jobs',
  careerAdviceRequest: 'career-advice-request',
  sourcesJobsJobCard: 'sources-jobs-job-card',
  sourcesJobsAllJobs: 'sources-jobs-all-jobs',
  sidebarLatestJob: 'sidebar-latest-job',
  sidebarLatestAllJobs: 'sidebar-latest-all-jobs',
  engagementModalAllJobs: 'engagement-modal-all-jobs',
} as const;

export const MAGAZIN_UTM_CAMPAIGN = {
  jobs: 'magazin_jobs',
  registration: 'magazin_registration',
} as const;

interface MagazinUtmParamsOptions {
  isInlineArticle?: boolean;
  category?: string;
  articleSlug?: string;
  /** Overrides the computed `utm_content` when provided. */
  utmContent?: string;
  /** Overrides the default `magazin_jobs` campaign when provided. */
  utmCampaign?: string;
}

export const generateMagazinUtmParams = (options?: MagazinUtmParamsOptions) => {
  const { articleSlug, category, isInlineArticle = false, utmContent: utmContentOverride, utmCampaign } = options || {};
  let utmContent = 'sidebar-latest';

  if (isDefined(category) && !isInlineArticle) {
    utmContent = `category-${category}`;
  } else if (isInlineArticle && isDefined(articleSlug)) {
    utmContent = 'inline-article';
  }

  if (isDefined(utmContentOverride)) utmContent = utmContentOverride;

  const utmParams = new URLSearchParams({
    /* eslint-disable eslint-compat/camelcase */
    utm_source: 'magazin',
    utm_medium: isDefined(articleSlug) ? 'article' : 'home-magazin',
    utm_campaign: utmCampaign ?? MAGAZIN_UTM_CAMPAIGN.jobs,
    utm_content: utmContent,
    /* eslint-enable eslint-compat/camelcase */
  });

  if (isDefined(articleSlug)) utmParams.set('utm_term', articleSlug);

  return utmParams;
};

export interface MagazinAttribution {
  articleSlug?: string;
  category?: string;
}

export const getMagazinUtmRecord = (attribution: MagazinAttribution, utmContent: string, utmCampaign?: string): Record<string, string> =>
  Object.fromEntries(
    generateMagazinUtmParams({
      articleSlug: attribution.articleSlug,
      category: attribution.category,
      utmContent,
      utmCampaign,
    }).entries(),
  );

export const getPosts = async (
  apolloClient: ApolloClient,
  page: number,
  limit?: number,
  category?: string,
  author?: string,
  contentType?: GhostContentType,
  featured?: boolean,
): Promise<PostsResponse> => {
  const posts = await apolloClient.query({
    fetchPolicy: isDefined(limit) ? 'cache-first' : 'no-cache',
    query: GET_GHOST_POSTS,
    variables: { page, limit, category, author, contentType, featured },
  });

  return {
    posts: posts.data?.getGhostPosts.posts ?? [],
    total: posts.data?.getGhostPosts.total ?? 0,
  };
};

export const getCategories = async (
  apolloClient: ApolloClient,
  allCategories: string,
  allSubCategories: string,
): Promise<CategoryType[]> => {
  const response = await apolloClient.query({
    query: GET_GHOST_CATEGORIES,
  });

  const categories = [
    { id: MAGAZIN_ALL_CATEGORIES_SLUG, name: allCategories, slug: MAGAZIN_ALL_CATEGORIES_SLUG },
    ...(response.data?.getGhostCategories.categories ?? []).map(category => ({
      ...category,
      subCategories: [
        { slug: MAGAZIN_ALL_CATEGORIES_SLUG, name: allSubCategories, ghostSlug: MAGAZIN_ALL_CATEGORIES_SLUG },
        ...(category.subCategories ?? []),
      ],
    })),
  ];
  return categories;
};

export const getAuthors = async (apolloClient: ApolloClient): Promise<AuthorType[]> => {
  const response = await apolloClient.query({
    fetchPolicy: 'no-cache',
    query: GET_GHOST_AUTHORS,
  });

  return response.data?.getGhostAuthors.authors ?? [];
};

export const getAuthorBySlug = async (apolloClient: ApolloClient, slug: string): Promise<AuthorType> => {
  const response = await apolloClient.query({
    fetchPolicy: 'no-cache',
    query: GET_GHOST_AUTHOR_BY_SLUG,
    variables: { slug },
  });

  if (!response.data) throw new Error(`Author not found: ${slug}`);

  return response.data.getGhostAuthorBySlug;
};
